import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sortable from 'sortablejs';
import {
  Stethoscope, Plus, Search, X, AlertCircle, CheckCircle, Edit2, RefreshCw,
  Briefcase, MoreHorizontal, ChevronUp, ChevronDown, Power, Banknote,
  Activity, Heart, Zap, FileText, Syringe, FlaskConical, Package, User, Users,
  GripVertical, ListOrdered
} from 'lucide-react';
import { medicalServiceAPI, doctorAPI } from '../services/api';
import { useToast } from './UIProvider';
import { toTitleCase } from '../utils/text';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import useDialog from '../hooks/useDialog';
import useKeyboardReorder from '../hooks/useKeyboardReorder';

// Alphabetical by label; OTHER pinned to the end.
const SERVICE_TYPE_CONFIG = {
  CONSULTATION: { label: 'Consultation', icon: FileText, color: 'bg-cyan-100 text-cyan-700', gradient: 'from-cyan-500 to-blue-600' },
  EMERGENCY: { label: 'Emergency', icon: Zap, color: 'bg-red-100 text-red-700', gradient: 'from-red-500 to-rose-600' },
  GYNECOLOGY_OPD: { label: 'Gynecology OPD', icon: Heart, color: 'bg-pink-100 text-pink-700', gradient: 'from-pink-500 to-rose-600' },
  LAB_TEST: { label: 'Lab Test', icon: FlaskConical, color: 'bg-amber-100 text-amber-700', gradient: 'from-amber-500 to-orange-600' },
  MEDICAL_OPD: { label: 'Medical OPD', icon: Stethoscope, color: 'bg-blue-100 text-blue-700', gradient: 'from-blue-500 to-indigo-600' },
  PROCEDURE: { label: 'Procedure', icon: Syringe, color: 'bg-purple-100 text-purple-700', gradient: 'from-purple-500 to-violet-600' },
  TREATMENT: { label: 'Treatment', icon: Activity, color: 'bg-emerald-100 text-emerald-700', gradient: 'from-emerald-500 to-teal-600' },
  OTHER: { label: 'Other', icon: Package, color: 'bg-gray-100 text-gray-700', gradient: 'from-gray-500 to-slate-600' }
};

const formatCurrency = (amt) => `Rs. ${Number(amt || 0).toLocaleString()}`;

const ActionMenu = ({ service, onEdit, onToggleActive, openMenuId, setOpenMenuId }) => {
  const menuRef = useRef(null);
  const isOpen = openMenuId === service.id;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    // Escape closes the menu and hands focus back to its button.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      menuRef.current?.querySelector('button')?.focus();
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [isOpen, setOpenMenuId]);

  return (
    // Choosing an item parks focus on the menu button first, so focus is not lost with the item.
    <div className="relative inline-block" ref={menuRef} onClickCapture={(e) => { const btn = e.currentTarget.querySelector('button'); if (btn && !btn.contains(e.target)) btn.focus(); }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : service.id); }}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreHorizontal className="h-4 w-4 text-gray-500" />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-20">
          {onEdit && (
            <button onClick={() => { onEdit(); setOpenMenuId(null); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
              <Edit2 className="h-4 w-4 text-gray-400" /><span>Edit</span>
            </button>
          )}
          {onEdit && onToggleActive && <div className="border-t border-gray-100 my-1"></div>}
          {onToggleActive && (
            <button
              onClick={() => { onToggleActive(); setOpenMenuId(null); }}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center space-x-2 ${service.isActive ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
            >
              <Power className="h-4 w-4" /><span>{service.isActive ? 'Deactivate' : 'Activate'}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MedicalServicesManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('SETTINGS_MEDICAL_SERVICES', action) : true),
    [hasPermission],
  );
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [scopeFilter, setScopeFilter] = useState('ALL'); // 'ALL' | 'GENERAL' | 'DOCTOR_SPECIFIC'
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [modal, setModal] = useState({ type: null, service: null });
  const [showReorder, setShowReorder] = useState(false);

  useEffect(() => { loadServices(); loadDoctors(); }, []);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3500);
  };

  const loadServices = async () => {
    setLoading(true); setError(null);
    try {
      const data = await medicalServiceAPI.getAllIncludingInactive();
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Failed to load medical services');
      setServices([]);
    } finally { setLoading(false); }
  };

  const loadDoctors = async () => {
    try {
      const data = await doctorAPI.getAll();
      setDoctors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load doctors:', err);
      setDoctors([]);
    }
  };

  const handleSave = async (data) => {
    try {
      if (modal.type === 'edit') {
        await medicalServiceAPI.update(modal.service.id, data);
        showToast('success', `"${data.name}" updated`);
      } else {
        await medicalServiceAPI.create(data);
        showToast('success', `"${data.name}" added`);
      }
      setModal({ type: null, service: null });
      loadServices();
    } catch (err) {
      showToast('error', 'Failed: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleToggleActive = async (service) => {
    try {
      if (service.isActive) {
        await medicalServiceAPI.deactivate(service.id);
        showToast('success', `"${service.name}" deactivated`);
      } else {
        await medicalServiceAPI.activate(service.id);
        showToast('success', `"${service.name}" activated`);
      }
      loadServices();
    } catch (err) {
      showToast('error', 'Failed: ' + (err?.message || 'Unknown error'));
    }
  };

  const filtered = services.filter(s => {
    const matchesSearch = !searchTerm ||
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.doctor && (s.doctor.fullName || `${s.doctor.firstName} ${s.doctor.lastName}`).toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = typeFilter === 'ALL' || s.serviceType === typeFilter;
    const matchesStatus =
      statusFilter === 'ALL' ||
      (statusFilter === 'ACTIVE' && s.isActive) ||
      (statusFilter === 'INACTIVE' && !s.isActive);
    const matchesScope =
      scopeFilter === 'ALL' ||
      (scopeFilter === 'GENERAL' && !s.doctor) ||
      (scopeFilter === 'DOCTOR_SPECIFIC' && !!s.doctor);
    return matchesSearch && matchesType && matchesStatus && matchesScope;
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[sortField] ?? '';
    let bVal = b[sortField] ?? '';
    if (sortField === 'baseFee') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3.5 w-3.5 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5 text-emerald-600" /> : <ChevronDown className="h-3.5 w-3.5 text-emerald-600" />;
  };

  const ariaSort = (field) => (sortField !== field ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending');

  // Stats
  const stats = {
    total: services.length,
    active: services.filter(s => s.isActive).length,
    inactive: services.filter(s => !s.isActive).length,
    totalValue: services.filter(s => s.isActive).reduce((sum, s) => sum + Number(s.baseFee || 0), 0),
  };

  const hasActiveFilters = searchTerm || typeFilter !== 'ALL' || statusFilter !== 'ALL';
  const paging = usePagedRows(sorted, {
    id: 'medical-services',
    resetOn: [sortField, sortDir, searchTerm, typeFilter, statusFilter, scopeFilter],
  });
  const clearFilters = () => { setSearchTerm(''); setTypeFilter('ALL'); setStatusFilter('ALL'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Briefcase className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Medical Services</h1>
                <p className="text-gray-600">Manage services and pricing available when issuing tokens</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Named for what it changes, not for the gesture: "Reorder" alone never said
                  reorder WHAT, or where the order shows up. */}
              {can('EDIT') && (
                <button
                  onClick={() => setShowReorder(true)}
                  className="flex items-center space-x-2 px-4 py-2.5 border border-emerald-300 text-emerald-700 bg-emerald-50/60 rounded-lg hover:bg-emerald-100 transition-colors font-medium"
                  title="Set the order these services appear in when issuing a token"
                >
                  <ListOrdered className="h-5 w-5" /><span>Set Service Order</span>
                </button>
              )}
              <button
                onClick={() => setModal({ type: 'add', service: null })}
                className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" /><span>Add New Service</span>
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {successMsg && (
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-6 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 text-emerald-600 mr-2" /><span className="text-emerald-700">{successMsg}</span>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center justify-between">
            <div className="flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><span className="text-red-700">{error}</span></div>
            <button onClick={loadServices} className="text-red-600 hover:text-red-800 font-medium text-sm">Retry</button>
          </div>
        )}

        {/* Stats — first three cards filter the table; Avg Fee is informational only. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Briefcase,   label: 'Total Services', value: stats.total,    gradient: 'from-blue-500 to-indigo-600',   status: 'ALL' },
            { icon: CheckCircle, label: 'Active',         value: stats.active,   gradient: 'from-emerald-500 to-green-600', status: 'ACTIVE' },
            { icon: Power,       label: 'Inactive',       value: stats.inactive, gradient: 'from-gray-400 to-gray-500',     status: 'INACTIVE' },
          ].map((s, i) => {
            const isActive = statusFilter === s.status && s.status !== 'ALL';
            const isTotalActive = s.status === 'ALL' && statusFilter === 'ALL';
            const toggle = () => {
              if (s.status === 'ALL') setStatusFilter('ALL');
              else setStatusFilter(isActive ? 'ALL' : s.status);
            };
            return (
              <button
                key={i}
                type="button"
                onClick={toggle}
                title={s.status === 'ALL' ? 'Show all services' : isActive ? 'Clear filter' : `Filter by ${s.label}`}
                className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all text-left ${
                  isActive ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                } ${isTotalActive ? 'ring-2 ring-blue-400' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center`}>
                    <s.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </button>
            );
          })}
          {/* Avg Fee — informational card, not clickable */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-gray-500 text-sm">Avg Fee (Active)</p><p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.active > 0 ? stats.totalValue / stats.active : 0)}</p></div>
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center"><Banknote className="h-6 w-6 text-white" /></div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            options={[{ value: 'ALL', label: 'All Types' }, ...Object.entries(SERVICE_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            className="min-w-[10rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]}
            className="min-w-[9rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={scopeFilter}
            onChange={setScopeFilter}
            options={[
              { value: 'ALL', label: 'All Doctors' },
              { value: 'GENERAL', label: 'General' },
              { value: 'DOCTOR_SPECIFIC', label: 'Doctor-specific' },
            ]}
            className="min-w-[11rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <button title="Refresh" onClick={loadServices} disabled={loading} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50">
            <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl">
          {!loading && <TablePager paging={paging} noun="service" filteredFrom={services.length} />}
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" aria-sort={ariaSort('name')}>
                  <button type="button" onClick={() => toggleSort('name')} className="flex w-full items-center space-x-1 uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><span>Service</span><SortIcon field="name" /></button>
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" aria-sort={ariaSort('serviceType')}>
                  <button type="button" onClick={() => toggleSort('serviceType')} className="flex w-full items-center space-x-1 uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><span>Type</span><SortIcon field="serviceType" /></button>
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Availability</th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" aria-sort={ariaSort('baseFee')}>
                  <button type="button" onClick={() => toggleSort('baseFee')} className="flex w-full items-center justify-end space-x-1 text-right uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><span>Fee</span><SortIcon field="baseFee" /></button>
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="h-5 w-40 bg-gray-200 rounded animate-pulse"></div></td>
                    <td className="px-6 py-4"><div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse"></div></td>
                    <td className="px-6 py-4"><div className="h-6 w-28 bg-gray-200 rounded-full animate-pulse"></div></td>
                    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded animate-pulse ml-auto"></div></td>
                    <td className="px-6 py-4"><div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse"></div></td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))
              ) : sorted.length > 0 ? (
                paging.pageRows.map((service) => {
                  const typeConfig = SERVICE_TYPE_CONFIG[service.serviceType] || SERVICE_TYPE_CONFIG.OTHER;
                  const TypeIcon = typeConfig.icon;
                  return (
                    <tr key={service.id} className={`hover:bg-gray-50 transition-colors ${!service.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 bg-gradient-to-br ${typeConfig.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{service.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                          <TypeIcon className="h-3 w-3" />{typeConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {service.doctor ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                            <User className="h-3 w-3" />
                            {service.doctor.fullName || `${service.doctor.firstName} ${service.doctor.lastName}`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                            <Users className="h-3 w-3" />
                            All doctors
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-semibold text-gray-900">{formatCurrency(service.baseFee)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${service.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${service.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                          {service.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <ActionMenu
                          service={service}
                          onEdit={can('EDIT') ? () => setModal({ type: 'edit', service }) : null}
                          onToggleActive={can('TOGGLE_ACTIVE') ? () => handleToggleActive(service) : null}
                          openMenuId={openMenuId}
                          setOpenMenuId={setOpenMenuId}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-500 mb-1">No Services Found</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {hasActiveFilters ? 'Try adjusting your search or filters' : 'Start by adding your first medical service'}
                    </p>
                    {!hasActiveFilters && (
                      <button onClick={() => setModal({ type: 'add', service: null })} className="inline-flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                        <Plus className="h-4 w-4" /><span>Add Medical Service</span>
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!loading && sorted.length > 0 && hasActiveFilters && (
            <div className="px-6 py-2 border-t border-gray-100 text-sm text-right">
              <button onClick={clearFilters} className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors">
                Clear filters
              </button>
            </div>
          )}
          {!loading && <TablePager paging={paging} noun="service" filteredFrom={services.length} position="bottom" />}
        </div>

        {/* Add/Edit Modal */}
        {modal.type && (
          <ServiceFormModal
            service={modal.service}
            doctors={doctors}
            onClose={() => setModal({ type: null, service: null })}
            onSubmit={handleSave}
          />
        )}

        {/* Reorder Modal — separate from the table because the table has its own column sorting,
            which would fight with a drag order applied in place. */}
        {showReorder && (
          <ReorderServicesModal
            services={services}
            doctors={doctors}
            onClose={() => { setShowReorder(false); loadServices(); }}
          />
        )}
      </div>
    </div>
  );
};

// The draggable list itself. In a doctor's scope each row carries a badge saying whether it is that
// doctor's own service or a general one shared with everybody, since both sit in the same list.
const SortableServiceList = ({ rows, startIndex = 0, onReorder, onMove, onCommit, showScopeBadge = false }) => {
  const rowsRef = useRef(rows);
  const onReorderRef = useRef(onReorder);
  const sortableRef = useRef(null);
  rowsRef.current = rows;
  onReorderRef.current = onReorder;

  const containerRef = useCallback((node) => {
    if (sortableRef.current) { sortableRef.current.destroy(); sortableRef.current = null; }
    if (!node) return;
    sortableRef.current = Sortable.create(node, {
      animation: 180,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
      forceFallback: true,
      fallbackTolerance: 4,
      filter: 'button',
      preventOnFilter: false,
      ghostClass: 'si-ghost',
      chosenClass: 'si-chosen',
      dragClass: 'si-drag',
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
        const next = [...(rowsRef.current || [])];
        const [moved] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, moved);
        onReorderRef.current?.(next);
      },
    });
  }, []);

  const handleProps = useKeyboardReorder({ items: rows, onMove, onCommit, getLabel: (s) => s.name });

  return (
    <div ref={containerRef} className="space-y-2">
      {rows.map((s, idx) => {
        const cfg = SERVICE_TYPE_CONFIG[s.serviceType] || SERVICE_TYPE_CONFIG.OTHER;
        const Icon = cfg.icon;
        return (
          <div key={s.id} className="si-row flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-grab hover:bg-gray-50/60">
            <span {...handleProps(s, idx)} className="inline-flex p-1 rounded text-gray-400 hover:text-gray-600 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 flex-shrink-0"><GripVertical className="h-4 w-4" /></span>
            <span className="w-6 text-xs font-semibold text-gray-400 flex-shrink-0">{startIndex + idx + 1}</span>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">{s.name}</span>
                {showScopeBadge && (
                  s.doctor ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium flex-none">Own</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 font-medium flex-none">General</span>
                  )
                )}
              </div>
              <div className="text-xs text-gray-400">{cfg.label}</div>
            </div>
            <span className="text-sm text-gray-600 flex-shrink-0">{formatCurrency(s.baseFee)}</span>
          </div>
        );
      })}
    </div>
  );
};

// Drag the order services appear in on the token-issue dropdown.
//
// General scope  — the default order every doctor inherits (one shared row per service).
// Doctor scope   — that doctor's WHOLE dropdown as one list: their own services and the general
//                  ones together, freely interleaved. Saving writes a per-doctor arrangement, so
//                  putting a general service first here moves it for this doctor only. The list is
//                  loaded from the same endpoint the token screen calls, so what is dragged is
//                  literally what the receptionist will see.
const ReorderServicesModal = ({ services = [], doctors = [], onClose }) => {
  const showToast = useToast();
  const [scope, setScope] = useState('GENERAL'); // 'GENERAL' | a doctor id
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasCustomOrder, setHasCustomOrder] = useState(false);
  const reorderDialogRef = useDialog(onClose, { confirmDiscard: false });
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const isDoctorScope = scope !== 'GENERAL';

  const sortedDoctors = [...doctors].sort((a, b) =>
    (a.fullName || `${a.firstName} ${a.lastName}`).localeCompare(b.fullName || `${b.firstName} ${b.lastName}`),
  );

  const loadScope = useCallback(async () => {
    if (scope === 'GENERAL') {
      // Unordered rows sort last, alphabetically, matching the server's ordering.
      const general = services
        .filter((s) => s.isActive && !s.doctor)
        .sort((a, b) => {
          const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
          const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
          return ao !== bo ? ao - bo : (a.name || '').localeCompare(b.name || '');
        });
      setRows(general);
      setHasCustomOrder(false);
      return;
    }
    setLoading(true);
    try {
      const [list, arranged] = await Promise.all([
        medicalServiceAPI.getForDoctor(scope),
        medicalServiceAPI.getDoctorOrder(scope),
      ]);
      setRows(Array.isArray(list) ? list.filter((s) => s.isActive) : []);
      setHasCustomOrder(Array.isArray(arranged) && arranged.length > 0);
    } catch (e) {
      setRows([]);
      showToastRef.current('error', e.message || "Failed to load this doctor's services");
    } finally {
      setLoading(false);
    }
  }, [scope, services]);

  useEffect(() => { loadScope(); }, [loadScope]);

  const saveOrder = (next) => {
    const ids = next.map((s) => s.id);
    const save = isDoctorScope
      ? medicalServiceAPI.reorderForDoctor(scope, ids)
      : medicalServiceAPI.reorder(ids);
    save
      .then(() => { if (isDoctorScope) setHasCustomOrder(true); })
      .catch((err) => showToastRef.current('error', err.message || 'Failed to save order'));
  };

  const handleReorder = (next) => { setRows(next); saveOrder(next); };

  const handleResetDoctorOrder = async () => {
    try {
      await medicalServiceAPI.clearDoctorOrder(scope);
      showToastRef.current('success', 'Back to the default order');
      loadScope();
    } catch (e) {
      showToastRef.current('error', e.message || 'Failed to reset the order');
    }
  };

  return (
    <div ref={reorderDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Set Service Order</h3>
            <p className="text-sm text-gray-500">The order these services appear in when issuing a token</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
          <Select
            value={scope}
            onChange={setScope}
            options={[
              { value: 'GENERAL', label: 'General services (all doctors)' },
              ...sortedDoctors.map((d) => ({
                value: d.id,
                label: d.fullName || (d.firstName || '') + ' ' + (d.lastName || ''),
              })),
            ]}
            buttonClassName="p-2.5 rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1.5">
            {isDoctorScope
              ? "This doctor's whole dropdown. Anything can go anywhere, and it applies to this doctor only."
              : 'The default order every doctor inherits, unless their own list has been arranged.'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">No active services in this scope.</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="flex items-center gap-2 text-xs text-emerald-700">
                  <GripVertical className="h-4 w-4 flex-shrink-0" /> Drag to reorder, or use the arrow keys on a row's handle. Changes save as you go.
                </span>
                {isDoctorScope && hasCustomOrder && (
                  <button
                    onClick={handleResetDoctorOrder}
                    className="text-xs font-medium text-gray-500 hover:text-red-600 hover:underline flex-none"
                  >
                    Reset to default order
                  </button>
                )}
              </div>

              {isDoctorScope && hasCustomOrder && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  This doctor has their own arrangement, so changes to the general order no longer reach them. Reset it to follow the general order again.
                </div>
              )}

              {/* Keyed by scope: changing scope remounts the list, so a keyboard move still waiting to save goes to the scope it was made in. */}
              <SortableServiceList key={scope} rows={rows} onReorder={handleReorder} onMove={setRows} onCommit={saveOrder} showScopeBadge={isDoctorScope} />
            </>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">Done</button>
        </div>
      </div>
    </div>
  );
};

const ServiceFormModal = ({ service, doctors = [], onClose, onSubmit }) => {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name: service?.name || '',
    description: service?.description || '',
    baseFee: service?.baseFee?.toString() || '',
    serviceType: service?.serviceType || 'MEDICAL_OPD',
    isActive: service?.isActive ?? true,
    // 'GENERAL' means available to all doctors. 'SPECIFIC' means tied to doctorId below.
    availability: service?.doctor ? 'SPECIFIC' : 'GENERAL',
    doctorId: service?.doctor?.id || '',
  });
  const [errors, setErrors] = useState({});
  const serviceDialogRef = useDialog(onClose);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Service name is required';
    if (!form.baseFee || isNaN(parseFloat(form.baseFee)) || parseFloat(form.baseFee) <= 0) {
      e.baseFee = 'Fee must be greater than 0';
    }
    if (!form.serviceType) e.serviceType = 'Service type is required';
    if (form.availability === 'SPECIFIC' && !form.doctorId) {
      e.doctorId = 'Select a doctor for this service';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim() || null,
      baseFee: parseFloat(form.baseFee),
      serviceType: form.serviceType,
      isActive: form.isActive,
      // Send the doctor as { id } — the backend resolves it to a User. null = general service.
      doctor: form.availability === 'SPECIFIC' && form.doctorId ? { id: form.doctorId } : null,
    });
  };

  return (
    <div ref={serviceDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                {isEdit ? <Edit2 className="h-6 w-6 text-white" /> : <Plus className="h-6 w-6 text-white" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Medical Service' : 'Add New Medical Service'}</h2>
                <p className="text-white/80 text-sm">{isEdit ? 'Update service details' : 'Create a new service for tokens'}</p>
              </div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Service Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onBlur={(e) => setForm(f => ({ ...f, name: toTitleCase(e.target.value) }))}
              className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
              placeholder="e.g., General OPD, Nebulization, Wound Dressing"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Base Fee (Rs) *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">Rs</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.baseFee}
                  onChange={(e) => setForm({ ...form, baseFee: e.target.value })}
                  className={`w-full pl-12 pr-4 p-3 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 ${errors.baseFee ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
                  placeholder="500"
                />
              </div>
              {errors.baseFee && <p className="text-red-500 text-xs mt-1">{errors.baseFee}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <Select
                value={form.isActive ? 'true' : 'false'}
                onChange={(v) => setForm({ ...form, isActive: v === 'true' })}
                options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
                buttonClassName="p-3 border-2 rounded-xl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Service Type *</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(SERVICE_TYPE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const isSelected = form.serviceType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({ ...form, serviceType: key })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                  >
                    <div className={`w-9 h-9 bg-gradient-to-br ${config.gradient} rounded-lg flex items-center justify-center mb-2`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div className={`text-xs font-semibold ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>{config.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Availability — who can bill this service */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Availability *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, availability: 'GENERAL', doctorId: '' })}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  form.availability === 'GENERAL'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users className={`h-4 w-4 ${form.availability === 'GENERAL' ? 'text-emerald-600' : 'text-gray-500'}`} />
                  <span className={`text-sm font-semibold ${form.availability === 'GENERAL' ? 'text-emerald-700' : 'text-gray-800'}`}>
                    General
                  </span>
                </div>
                <p className="text-xs text-gray-500">Available for every doctor</p>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, availability: 'SPECIFIC' })}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  form.availability === 'SPECIFIC'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <User className={`h-4 w-4 ${form.availability === 'SPECIFIC' ? 'text-emerald-600' : 'text-gray-500'}`} />
                  <span className={`text-sm font-semibold ${form.availability === 'SPECIFIC' ? 'text-emerald-700' : 'text-gray-800'}`}>
                    Doctor-specific
                  </span>
                </div>
                <p className="text-xs text-gray-500">Only for the selected doctor</p>
              </button>
            </div>
            {form.availability === 'SPECIFIC' && (
              <div className="mt-3">
                <Select
                  value={form.doctorId}
                  onChange={(v) => setForm({ ...form, doctorId: v })}
                  placeholder="Select a doctor…"
                  error={errors.doctorId ? ' ' : undefined}
                  options={doctors.map((d) => ({
                    value: d.id,
                    label: (d.fullName || 'Dr. ' + d.firstName + ' ' + d.lastName)
                      + (d.specialization ? ' - ' + d.specialization : ''),
                  }))}
                  buttonClassName="p-3 border-2 rounded-xl"
                />
                {errors.doctorId && <p className="text-red-500 text-xs mt-1">{errors.doctorId}</p>}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="Additional details about this service..."
            />
          </div>
        </div>

        <div className="px-6 py-5 bg-gray-50 rounded-b-3xl flex justify-end space-x-3">
          <button onClick={onClose} className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors">Cancel</button>
          <button onClick={handleSubmit} className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors">
            {isEdit ? 'Save Changes' : 'Add Service'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicalServicesManagement;
